<template>
  <div id="qualification_average_by_project_type">
    <vx-card title="Promedio de calificación por tipo">
      <!-- LOADING -->
      <div
        v-if="uiState === 'loading'"
        class="gw-empty-state"
        style="height:350px;"
      >
        <div class="gw-spinner" aria-label="Cargando"></div>
        <div class="gw-empty-title">Cargando información…</div>
        <div class="gw-empty-subtitle">Un momento por favor</div>
      </div>

      <!-- READY (CHART) -->
      <apexcharts
        v-else-if="uiState === 'ready' && renderGraphic"
        type="bar"
        height="350"
        :options="chartOptions"
        :series="series"
      />

      <!-- EMPTY / ERROR -->
      <div
        v-else
        class="gw-empty-state"
        style="height:350px;"
      >
        <img
          class="gw-empty-img"
          :src="emptyStateImage"
          :alt="emptyStateAlt"
        />
        <div class="gw-empty-title">{{ emptyStateTitle }}</div>
        <div class="gw-empty-subtitle">{{ emptyStateSubtitle }}</div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { publicAssetUrl } from '@/utils/publicAssets'
import { eventBus } from "./../../main";
import VueApexCharts from "vue-apexcharts";
import { mapGetters } from "vuex";
import api from "./../../mixins/api";

export default {
  props: ["startDate", "endDate", "cityId"],
  mixins: [api],

  data() {
    return {
      // UI states: 'loading' | 'ready' | 'empty' | 'error'
      uiState: "loading",
      renderGraphic: false,

      requestKey: null,
      consultHandler: null,

      // Empty/Error illustrations (S3)
      IMG_EMPTY:
        publicAssetUrl('Assets/illustrations/no_data_found.png'),
      IMG_ERROR:
        publicAssetUrl('Assets/illustrations/error.png'),

      chartOptions: {
        chart: { height: 350, type: "bar" },
        plotOptions: { bar: { columnWidth: "50%" } },
        dataLabels: { enabled: false },
        stroke: { width: 2 },
        grid: { row: { colors: ["#fff", "#f2f2f2"] } },
        colors: ["#5F0091"],
        xaxis: { categories: [] },
        yaxis: { title: { text: "Promedio de calificación por tipo" } },
      },

      series: [
        {
          name: "Promedio",
          data: [],
        },
      ],
    };
  },

  components: {
    apexcharts: VueApexCharts,
  },

  computed: {
    ...mapGetters(["userInfo"]),

    emptyStateImage() {
      return this.uiState === "error" ? this.IMG_ERROR : this.IMG_EMPTY;
    },

    emptyStateAlt() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "No se encontraron resultados";
    },

    emptyStateTitle() {
      return this.uiState === "error"
        ? "Se produjo un error con la consulta"
        : "Sin datos para el filtro seleccionado";
    },

    emptyStateSubtitle() {
      return this.uiState === "error"
        ? "Inténtalo de nuevo o ajusta los filtros."
        : "Prueba con otro rango de fechas o ciudad.";
    },
  },

  methods: {
    getUrl() {
      var path =
        "/metrics/qualification-average?client_id=" +
        this.userInfo.user.client_id;

      var url = "";

      if (this.startDate != null && this.endDate != null) {
        var params =
          "&start_date=" + this.startDate + "&end_date=" + this.endDate;
        url = this.url() + path + params;
      } else {
        url = this.url() + path;
      }

      if (this.cityId != null) url += "&city_id=" + this.cityId;

      return url;
    },

    resetChart() {
      this.renderGraphic = false;
      this.series = [{ name: "Promedio", data: [] }];
      this.chartOptions.xaxis = { categories: [] };
    },

    setLoading(key) {
      this.requestKey = key;
      this.uiState = "loading";
      this.resetChart();
    },

    setEmpty() {
      this.uiState = "empty";
      this.resetChart();
    },

    setError() {
      this.uiState = "error";
      this.resetChart();
    },

    async setReady(categories, seriesData) {
      // Remount para evitar glitches en ApexCharts
      this.renderGraphic = false;
      await this.$nextTick();

      this.chartOptions.xaxis = { categories: categories };
      this.series = [{ name: "Promedio", data: seriesData }];

      this.renderGraphic = true;
      this.uiState = "ready";
    },

    async initializeData(origin) {
      if (!origin) origin = "unknown";

      var key =
        (this.startDate || "") +
        "|" +
        (this.endDate || "") +
        "|" +
        (this.cityId || "");

      this.setLoading(key);

      console.log("[qualification-average] initializeData:", {
        origin: origin,
        startDate: this.startDate,
        endDate: this.endDate,
        cityId: this.cityId,
        url: this.getUrl(),
      });

      try {
        var request = await this.requestGetWithoutContent(this.getUrl(), {});

        // evitar race conditions
        if (this.requestKey !== key) {
          console.log("[qualification-average] stale response ignored");
          return;
        }

        if (!request || request.status !== 200) {
          console.warn(
            "[qualification-average] request not ok:",
            request && request.status
          );
          this.setError();
          return;
        }

        var data = request.data;

        if (!Array.isArray(data) || data.length === 0) {
          console.warn("[qualification-average] Empty data");
          this.setEmpty();
          return;
        }

        // construir categorías + valores válidos
        var categories = [];
        var seriesData = [];

        for (var i = 0; i < data.length; i++) {
          var item = data[i] || {};
          categories.push(this.$t(item.name));

          var v = item.round;
          if (typeof v === "string") v = parseFloat(v);
          v = typeof v === "number" && !isNaN(v) ? v : null;

          // Si viene null/NaN, ponemos 0 para mantener alineación con categorías
          seriesData.push(v === null ? 0 : v);
        }

        // si todo es 0 => empty (no vale la pena renderizar)
        var hasNonZero = seriesData.some(function (x) {
          return typeof x === "number" && x > 0;
        });

        if (!hasNonZero) {
          console.warn("[qualification-average] all zeros");
          this.setEmpty();
          return;
        }

        console.log("[qualification-average] xaxis:", categories);
        console.log("[qualification-average] series:", seriesData);

        await this.setReady(categories, seriesData);
      } catch (e) {
        if (this.requestKey !== key) return;
        console.warn("[qualification-average] request exception:", e);
        this.setError();
      }
    },
  },

  mounted() {
    this.initializeData("mounted");

    this.consultHandler = () =>
      this.initializeData("eventBus:consultData");

    eventBus.$on("consultData", this.consultHandler);
  },

  beforeDestroy() {
    if (this.consultHandler) {
      eventBus.$off("consultData", this.consultHandler);
    }
  },
};
</script>

<style scoped>
.gw-empty-state{
  display:flex;
  flex-direction:column;
  align-items:center;
  justify-content:center;
  gap: 10px;
  text-align:center;
  padding: 16px;
}

.gw-empty-img{
  max-width: 260px;
  width: 100%;
  height: auto;
}

.gw-empty-title{
  font-weight: 600;
  font-size: 14px;
  color: #4b4b4b;
}

.gw-empty-subtitle{
  font-size: 12px;
  color: #7a7a7a;
}

.gw-spinner{
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(95,0,145,0.18);
  border-top-color: rgba(95,0,145,0.9);
  animation: gwspin 0.9s linear infinite;
}

@keyframes gwspin{
  to { transform: rotate(360deg); }
}
</style>
